import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import {
  Mail,
  MoveRight,
  NotebookPen,
  Briefcase,
  XCircle,
  Heart,
  Building2,
  Send,
  Edit2,
  Archive,
  Clock,
  Filter,
  CreditCard,
  TimerOff,
  UserPlus,
  Columns3,
  FileText,
  CheckCircle,
  StickyNote,
  Award,
  MessageSquare,
  Search,
  CalendarDays,
} from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import sh from "bundles/common/utils/sh";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import DateRangeSelect from "bundles/Internal/components/common/DateRangeSelect";

const PAGE_SIZE = 50;

const C = {
  rowHover: "hsla(140, 8%, 90%, 0.35)",
};

// Candidate names link through to the internal candidate page. Kept inline per panel
// rather than shared, so each timeline owns its own presentation.
const CandidateLink = ({ payload, fallback = "Unknown" }) => {
  const navigate = useNavigate();
  const label = payload?.candidate_name || fallback;
  const boldSx = { fontFamily: "Segoe UI Semibold, sans-serif, system-ui" };

  // External candidates have no internal page, so they stay plain text.
  if (!payload?.candidate_id) {
    return <Box component="span" sx={boldSx}>{label}</Box>;
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/internal/candidates/${payload.candidate_id}`);
      }}
      sx={{
        ...boldSx,
        p: 0,
        border: "none",
        background: "none",
        font: "inherit",
        fontSize: "inherit",
        cursor: "pointer",
        color: "hsl(150 38% 30%)",
        "&:hover": { textDecoration: "underline" },
        "&:focus-visible": { outline: "2px solid hsl(150 38% 30%)", outlineOffset: 2 },
      }}
    >
      {label}
    </Box>
  );
};

const EVENT_CONFIG = {
  note: { icon: NotebookPen, label: "Note", color: "hsl(150 38% 17%)", bg: "hsl(150 30% 95%)" },
  recruiter_note: { icon: NotebookPen, label: "Rec. note", color: "hsl(200 45% 30%)", bg: "hsl(200 35% 95%)" },
  email: { icon: Mail, label: "Email", color: "hsl(217 70% 30%)", bg: "hsl(217 50% 95%)" },
  job_offer_published: { icon: CheckCircle, label: "Published", color: "hsl(142 50% 30%)", bg: "hsl(142 40% 95%)" },
  job_offer_closed: { icon: XCircle, label: "Closed", color: "hsl(0 55% 38%)", bg: "hsl(0 40% 95%)" },
  job_application_received: { icon: Briefcase, label: "Applied", color: "hsl(180 35% 28%)", bg: "hsl(180 30% 95%)" },
  job_application_closed: { icon: XCircle, label: "App closed", color: "hsl(0 45% 42%)", bg: "hsl(0 35% 95%)" },
  candidate_interest: { icon: Heart, label: "Interest", color: "hsl(330 50% 40%)", bg: "hsl(330 40% 95%)" },
  kanban_move: { icon: MoveRight, label: "Kanban", color: "hsl(38 70% 30%)", bg: "hsl(38 50% 95%)" },
  suggestion_received: { icon: Building2, label: "Suggestion", color: "hsl(38 60% 40%)", bg: "hsl(38 45% 95%)" },
  credit_purchased: { icon: CreditCard, label: "Credit+", color: "hsl(262 50% 35%)", bg: "hsl(262 40% 95%)" },
  credit_expired: { icon: TimerOff, label: "Credit exp.", color: "hsl(30 60% 35%)", bg: "hsl(30 45% 95%)" },
  recruiter_joined: { icon: UserPlus, label: "Joined", color: "hsl(200 60% 35%)", bg: "hsl(200 45% 95%)" },
  kanban_column_created: { icon: Columns3, label: "Column+", color: "hsl(270 40% 38%)", bg: "hsl(270 30% 95%)" },
  candidate_note_written: { icon: StickyNote, label: "Cand. note", color: "hsl(45 60% 35%)", bg: "hsl(45 45% 95%)" },
  milestone_added: { icon: Award, label: "Milestone", color: "hsl(280 45% 38%)", bg: "hsl(280 35% 95%)" },
  testimonial_added: { icon: MessageSquare, label: "Testimonial", color: "hsl(170 45% 30%)", bg: "hsl(170 35% 95%)" },
};

const FILTERS = [
  { key: "all", label: "All", icon: Filter, color: "hsl(150 38% 17%)", bg: "hsl(140 10% 95%)" },
  { key: "notes", label: "Notes", icon: NotebookPen, color: "hsl(150 38% 17%)", bg: "hsl(150 30% 95%)" },
  { key: "email", label: "Emails", icon: Mail, color: "hsl(217 70% 30%)", bg: "hsl(217 50% 95%)" },
  { key: "job_offers", label: "Job offers", icon: FileText, color: "hsl(142 50% 30%)", bg: "hsl(142 40% 95%)" },
  { key: "applications", label: "Applications", icon: Briefcase, color: "hsl(180 35% 28%)", bg: "hsl(180 30% 95%)" },
  { key: "kanban", label: "Kanban", icon: MoveRight, color: "hsl(38 70% 30%)", bg: "hsl(38 50% 95%)" },
  { key: "credits", label: "Credits", icon: CreditCard, color: "hsl(262 50% 35%)", bg: "hsl(262 40% 95%)" },
  { key: "other", label: "Other", icon: Archive, color: "hsl(150 10% 46%)", bg: "hsl(140 10% 95%)" },
];

const NOTE_TYPES = ["note", "recruiter_note"];
const JOB_OFFER_TYPES = ["job_offer_published", "job_offer_closed"];
const KANBAN_TYPES = ["kanban_move", "kanban_column_created"];
const CREDIT_TYPES = ["credit_purchased", "credit_expired"];
const APPLICATION_TYPES = ["job_application_received", "job_application_closed"];
const OTHER_TYPES = ["candidate_interest", "suggestion_received", "candidate_note_written", "recruiter_joined", "milestone_added", "testimonial_added"];

// Event types behind each filter chip; the server filters on these
const FILTER_TYPES = {
  notes: NOTE_TYPES,
  email: ["email"],
  job_offers: JOB_OFFER_TYPES,
  applications: APPLICATION_TYPES,
  kanban: KANBAN_TYPES,
  credits: CREDIT_TYPES,
  other: OTHER_TYPES,
};

const sumCounts = (counts, types) => types.reduce((sum, type) => sum + (counts[type] || 0), 0);

const formatRelative = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
};

const formatFull = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDateGroup = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (eventDay.getTime() === today.getTime()) return "Today";
  if (eventDay.getTime() === yesterday.getTime()) return "Yesterday";
  if (today.getTime() - eventDay.getTime() < 7 * 86400000) {
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
};

const groupEventsByDate = (events) => {
  const groups = [];
  let currentLabel = null;
  for (const event of events) {
    const label = getDateGroup(event.occurred_at);
    if (label !== currentLabel) {
      groups.push({ label, events: [event] });
      currentLabel = label;
    } else {
      groups[groups.length - 1].events.push(event);
    }
  }
  return groups;
};

const EventIcon = ({ type, isLast }) => {
  const config = EVENT_CONFIG[type] || { icon: NotebookPen, color: "hsl(150 10% 46%)", bg: "hsl(140 10% 95%)" };
  const Icon = config.icon;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: config.bg,
          border: `1.5px solid ${config.color}30`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <Icon size={14} color={config.color} strokeWidth={2.2} />
      </Box>
      {!isLast && (
        <Box
          sx={{
            width: 1.5,
            flex: 1,
            backgroundColor: "hsl(140 8% 88%)",
            minHeight: 16,
          }}
        />
      )}
    </Box>
  );
};

const EMAIL_TITLE_LABELS = {
  confirmation_instructions: "Email confirmation",
  reminder_confirmation_instructions: "Confirmation reminder",
  invitation_instructions: "Invitation to join",
  subscription_confirmation: "Subscription confirmed",
  order_summary: "Order summary",
  job_offer_published: "Job offer published notification",
  new_job_application: "New application received",
  expired_job_offers: "Job offer expired",
  forgot_password: "Password reset",
  confirmation_password_modification: "Password changed",
  weekly_report: "Weekly report",
  contact_email: "Contact message",
  job_application_confirmation: "Application confirmation",
  spontaneous_job_application_confirmation: "Spontaneous application confirmed",
  job_regional: "Regional job suggestions",
  monthly_suggestion: "Monthly job suggestions",
};

const EmailEventBody = ({ payload }) => (
  <Box>
    {(payload.recruiter_name || payload.recruiter_email) && (
      <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mb: 0.25 }}>
        To {payload.recruiter_name}{payload.recruiter_email && ` (${payload.recruiter_email})`}
      </Typography>
    )}
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      {EMAIL_TITLE_LABELS[payload.email_title] || payload.email_title}
    </Typography>
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
      {payload.sent && (
        <Chip label="Sent" size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(150 30% 93%)", color: "hsl(150 38% 25%)" }} />
      )}
      {payload.delivered && (
        <Chip label="Delivered" size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(200 40% 93%)", color: "hsl(200 50% 30%)" }} />
      )}
      {payload.hard_bounced && (
        <Chip label="Bounced" size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(0 50% 93%)", color: "hsl(0 55% 38%)" }} />
      )}
      {payload.open_count > 0 && (
        <Chip label={`Opened ${payload.open_count}x`} size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(262 30% 93%)", color: "hsl(262 40% 35%)" }} />
      )}
      {payload.click_count > 0 && (
        <Chip label={`Clicked ${payload.click_count}x`} size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(38 40% 93%)", color: "hsl(38 50% 30%)" }} />
      )}
      {payload.unsubscribed && (
        <Chip label="Unsubscribed" size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(30 60% 93%)", color: "hsl(30 60% 30%)" }} />
      )}
    </Stack>
  </Box>
);

const NoteEventBody = ({ payload, noteId, onEdit, onArchive }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(payload.content);

  const handleSave = () => {
    if (editContent.trim() && editContent !== payload.content) {
      onEdit(noteId, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(payload.content);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box>
        <TextField
          fullWidth
          multiline
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          autoFocus
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: 12.5,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              borderRadius: "10px",
              backgroundColor: "hsl(140 10% 95%)",
              "& fieldset": { borderColor: "hsl(140 8% 88%)" },
              "&:hover fieldset": { borderColor: "hsl(150 10% 46%)" },
              "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 1.5 },
            },
          }}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            disableRipple
            onClick={handleSave}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "8px",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              boxShadow: "none",
              "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9, boxShadow: "none" },
            }}
          >
            Save
          </Button>
          <Button
            size="small"
            disableRipple
            onClick={handleCancel}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "8px",
              color: "hsl(150 10% 46%)",
              "&:hover": { backgroundColor: "hsl(140 10% 95%)" },
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.5}>
      <Typography
        sx={{
          fontSize: 12.5,
          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
          color: "hsl(150 30% 12%)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.6,
          flex: 1,
        }}
      >
        {payload.content}
      </Typography>
      <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, mt: -0.25 }}>
        {payload.editable && (
          <Tooltip title="Edit note" arrow placement="top">
            <IconButton
              className="note-action-btn"
              size="small"
              onClick={() => setIsEditing(true)}
              sx={{
                p: 0.5,
                borderRadius: "8px",
                color: "hsl(150 10% 60%)",
                opacity: 0,
                transition: "all 0.15s ease",
                "&:hover": { backgroundColor: "hsl(140 10% 95%)", color: "hsl(150 30% 12%)" },
              }}
            >
              <Edit2 size={13} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Archive note" arrow placement="top">
          <IconButton
            className="note-action-btn"
            size="small"
            onClick={() => onArchive(noteId)}
            sx={{
              p: 0.5,
              borderRadius: "8px",
              color: "hsl(150 10% 60%)",
              opacity: 0,
              transition: "all 0.15s ease",
              "&:hover": { backgroundColor: "hsl(0 50% 96%)", color: "hsl(0 55% 45%)" },
            }}
          >
            <Archive size={13} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

const JobOfferPublishedBody = ({ payload }) => (
  <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
    <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.job_title}</Box>
    {payload.offer_status && (
      <Box component="span" sx={{ color: "hsl(150 30% 12%)" }}> ({payload.offer_status})</Box>
    )}
  </Typography>
);

const JobOfferClosedBody = ({ payload }) => (
  <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
    <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.job_title}</Box>
  </Typography>
);

const InterestBody = ({ payload }) => (
  <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
    <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.recruiter_name || "Unknown"}</Box>
    {" revealed "}
    <CandidateLink payload={payload} />
  </Typography>
);

const STAGE_COLORS = {
  hired: "hsl(142 71% 45%)",
  pending: "hsl(217 91% 55%)",
  default: "hsl(38 70% 45%)",
};

const KanbanMoveBody = ({ payload }) => {
  const stages = payload.all_stages || [];
  const toIdx = stages.findIndex((s) => s.name === payload.to_column);
  const fromIdx = stages.findIndex((s) => s.name === payload.from_column);
  const isAdvance = fromIdx === -1 || toIdx >= fromIdx;
  const accent = STAGE_COLORS[payload.to_column_key] || STAGE_COLORS.default;
  const accentFaded = accent.replace(")", " / 0.3)");
  const offerTitles = payload.job_offer_titles || [];

  const candidateBold = <CandidateLink payload={payload} fallback="Unknown candidate" />;
  const stageBold = (
    <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: accent }}>
      {payload.to_column}
    </Box>
  );

  let description;
  if (payload.to_column_key === "hired") {
    description = <>{candidateBold}{" was hired!"}</>;
  } else if (payload.to_column_key === "pending") {
    description = <>{candidateBold}{" moved to "}{stageBold}</>;
  } else if (isAdvance) {
    description = <>{candidateBold}{" advanced to "}{stageBold}</>;
  } else {
    description = <>{candidateBold}{" moved to "}{stageBold}</>;
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
        {description}
      </Typography>

      {offerTitles.length > 0 && (
        <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", mt: 0.25 }}>
          {offerTitles.length === 1 ? offerTitles[0] : offerTitles.join(" · ")}
        </Typography>
      )}

      {stages.length > 0 && toIdx !== -1 && (
        <Box sx={{ mt: 1, display: "flex", gap: "3px" }}>
          {stages.map((stage, i) => {
            const isCurrent = i === toIdx;
            const isPast = i < toIdx;
            const barColor = isCurrent ? accent : isPast ? accentFaded : "hsl(140 8% 88%)";
            return (
              <Box key={i} sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    height: isCurrent ? 4 : 3,
                    borderRadius: 2,
                    backgroundColor: barColor,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 9,
                    mt: 0.25,
                    textAlign: "center",
                    color: isCurrent ? accent : isPast ? "hsl(150 10% 46%)" : "hsl(150 10% 60%)",
                    fontFamily: isCurrent
                      ? "Segoe UI Semibold, sans-serif, system-ui"
                      : "Segoe UI Regular, sans-serif, system-ui",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {stage.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {(stages.length === 0 || toIdx === -1) && (
        <Typography sx={{ fontSize: 11.5, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mt: 0.25 }}>
          {payload.from_column || "None"} → {payload.to_column || "None"}
        </Typography>
      )}
    </Box>
  );
};

const SUGGESTION_STATUS_COLORS = {
  rejected: { bg: "hsl(0 45% 93%)", color: "hsl(0 55% 38%)" },
  saved: { bg: "hsl(150 35% 93%)", color: "hsl(150 45% 25%)" },
};

const SuggestionBody = ({ payload }) => {
  const statusColors = SUGGESTION_STATUS_COLORS[payload.status] || { bg: "hsl(140 10% 95%)", color: "hsl(150 10% 46%)" };
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
        <CandidateLink payload={payload} />
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        {payload.status && (
          <Chip label={payload.status} size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: statusColors.bg, color: statusColors.color }} />
        )}
        {payload.selected_by && (
          <Chip label={`by ${payload.selected_by}`} size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(140 10% 95%)", color: "hsl(150 10% 46%)" }} />
        )}
      </Stack>
    </Box>
  );
};

const CREDIT_TYPE_LABELS = {
  job_offer_creation: "Job offer credit",
  cv_access: "CV access credit",
  licence: "Licence credit",
};

const CreditPurchasedBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      {CREDIT_TYPE_LABELS[payload.credit_type] || payload.credit_type}
    </Typography>
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
      {payload.status && (
        <Chip label={payload.status} size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(262 30% 93%)", color: "hsl(262 40% 35%)" }} />
      )}
      {payload.expire_at && (
        <Chip label={`Expires ${new Date(payload.expire_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`} size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(140 10% 95%)", color: "hsl(150 10% 46%)" }} />
      )}
    </Stack>
  </Box>
);

const CreditExpiredBody = ({ payload }) => (
  <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
    {CREDIT_TYPE_LABELS[payload.credit_type] || payload.credit_type}
  </Typography>
);

const KanbanColumnBody = ({ payload }) => (
  <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
    <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.column_name}</Box>
    {payload.key && (
      <Box component="span" sx={{ color: "hsl(150 10% 46%)" }}> ({payload.key})</Box>
    )}
  </Typography>
);

const CandidateNoteBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      On <CandidateLink payload={payload} />
      {payload.recruiter_name && (
        <Box component="span" sx={{ color: "hsl(150 10% 46%)" }}> by {payload.recruiter_name}</Box>
      )}
    </Typography>
    <Box sx={{ mt: 0.5, pl: 1.25, borderLeft: `2px solid ${"hsl(140 8% 88%)"}` }}>
      <Typography sx={{ fontSize: 11.5, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", lineHeight: 1.5 }}>
        {payload.content}
      </Typography>
    </Box>
  </Box>
);

const RecruiterNoteBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      On <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.recruiter_name || "Unknown"}</Box>
    </Typography>
    <Box sx={{ mt: 0.5, pl: 1.25, borderLeft: `2px solid ${"hsl(140 8% 88%)"}` }}>
      <Typography sx={{ fontSize: 11.5, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", lineHeight: 1.5 }}>
        {payload.content}
      </Typography>
    </Box>
  </Box>
);

const RecruiterJoinedBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.recruiter_name}</Box>
    </Typography>
    {payload.email && (
      <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
        {payload.email}
      </Typography>
    )}
  </Box>
);

const MilestoneBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.title}</Box>
    </Typography>
    {payload.year && (
      <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
        Year: {payload.year}
      </Typography>
    )}
  </Box>
);

const TestimonialBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.author_name || "Unknown"}</Box>
      {payload.role && (
        <Box component="span" sx={{ color: "hsl(150 10% 46%)" }}> ({payload.role})</Box>
      )}
    </Typography>
    {payload.content && (
      <Box sx={{ mt: 0.5, pl: 1.25, borderLeft: `2px solid ${"hsl(140 8% 88%)"}` }}>
        <Typography sx={{ fontSize: 11.5, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", lineHeight: 1.5 }}>
          {payload.content}
        </Typography>
      </Box>
    )}
  </Box>
);

const AppReceivedBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      <CandidateLink payload={payload} />
      {" applied for "}
      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.job_title}</Box>
    </Typography>
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
      {payload.sent_from_talent_team && (
        <Chip label="Talent team" size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(262 30% 93%)", color: "hsl(262 40% 35%)" }} />
      )}
      {payload.added_from_cvtheque && (
        <Chip label="CVthèque" size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(38 40% 93%)", color: "hsl(38 50% 30%)" }} />
      )}
    </Stack>
  </Box>
);

const AppClosedBody = ({ payload }) => (
  <Box>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.5 }}>
      <CandidateLink payload={payload} />
      {" · "}
      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{payload.job_title}</Box>
    </Typography>
    {payload.denial_reasons?.length > 0 && (
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
        {payload.denial_reasons.map((r, i) => (
          <Chip key={i} label={r} size="small" sx={{ height: 20, fontSize: 10.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", borderRadius: "999px", backgroundColor: "hsl(0 40% 95%)", color: "hsl(0 45% 40%)" }} />
        ))}
      </Stack>
    )}
  </Box>
);

const EVENT_BODY = {
  note: NoteEventBody,
  recruiter_note: RecruiterNoteBody,
  email: EmailEventBody,
  job_offer_published: JobOfferPublishedBody,
  job_offer_closed: JobOfferClosedBody,
  job_application_received: AppReceivedBody,
  job_application_closed: AppClosedBody,
  candidate_interest: InterestBody,
  kanban_move: KanbanMoveBody,
  suggestion_received: SuggestionBody,
  credit_purchased: CreditPurchasedBody,
  credit_expired: CreditExpiredBody,
  recruiter_joined: RecruiterJoinedBody,
  kanban_column_created: KanbanColumnBody,
  candidate_note_written: CandidateNoteBody,
  milestone_added: MilestoneBody,
  testimonial_added: TestimonialBody,
};

const EventRow = ({ event, onEditNote, onArchiveNote, isLast, delay, animated }) => {
  const config = EVENT_CONFIG[event.type] || { label: event.type, color: "hsl(150 10% 46%)" };
  const BodyComponent = EVENT_BODY[event.type];

  return (
    <Box
      component={motion.div}
      initial={animated ? { opacity: 0, y: 12 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut", delay }}
      sx={{
        display: "flex",
        gap: 1.5,
        pr: 2,
        pl: 0.5,
        "&:hover": {
          "& .event-content": { backgroundColor: "hsla(140, 8%, 90%, 0.35)", borderColor: "hsl(140 8% 88%)" },
          "& .note-action-btn": { opacity: 1 },
        },
      }}
    >
      <EventIcon type={event.type} isLast={isLast} />
      <Box
        className="event-content"
        sx={{
          flex: 1,
          minWidth: 0,
          pb: isLast ? 0 : 2,
          pt: 0.25,
          borderRadius: "10px",
          px: 1.25,
          py: 1,
          mb: isLast ? 0 : 0.5,
          transition: "background-color 0.15s ease, border-color 0.15s ease",
          border: "1px solid transparent",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Chip
            label={config.label}
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              letterSpacing: 0.3,
              textTransform: "uppercase",
              borderRadius: "calc(-2px + 0.75rem)",
              backgroundColor: `${config.color}12`,
              color: config.color,
              border: `1px solid ${config.color}20`,
            }}
          />
          {event.actor?.name && (
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
              {event.actor.name}
            </Typography>
          )}
          <Tooltip title={formatFull(event.occurred_at)} arrow placement="top">
            <Typography
              sx={{
                fontSize: 11,
                color: "hsl(150 10% 46%)",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                flexShrink: 0,
                ml: "auto !important",
                cursor: "default",
              }}
            >
              {formatRelative(event.occurred_at)}
            </Typography>
          </Tooltip>
        </Stack>
        {BodyComponent && (
          <Box sx={{ mt: 0.75 }}>
            {event.type === "note" ? (
              <BodyComponent
                payload={event.payload}
                noteId={event.id.replace("note_", "")}
                onEdit={onEditNote}
                onArchive={onArchiveNote}
              />
            ) : (
              <BodyComponent payload={event.payload} />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const DateGroupHeader = ({ label, delay, animated }) => (
  <Box
    component={motion.div}
    initial={animated ? { opacity: 0, x: -8 } : false}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3, ease: "easeOut", delay }}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2,
      pt: 2,
      pb: 1,
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        color: "hsl(150 10% 46%)",
        textTransform: "uppercase",
        letterSpacing: 0.8,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", backgroundColor: "hsl(140 8% 88%)" }} />
  </Box>
);

const FeedSkeleton = ({ rows = 5 }) => (
  <Box sx={{ px: 2, pt: 2 }}>
    {Array.from({ length: rows }, (_, i) => i).map((i) => (
      <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
        <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0, bgcolor: "hsl(140 8% 92%)" }} />
        <Box sx={{ flex: 1, pt: 0.5 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 0.75 }}>
            <Skeleton width={56} height={20} sx={{ borderRadius: "10px", bgcolor: "hsl(140 8% 92%)" }} />
            <Skeleton width={80} height={14} sx={{ borderRadius: "4px", bgcolor: "hsl(140 8% 92%)" }} />
          </Stack>
          <Skeleton width="70%" height={14} sx={{ borderRadius: "4px", bgcolor: "hsl(140 8% 92%)" }} />
        </Box>
      </Box>
    ))}
  </Box>
);

const EmptyFeed = ({ filtered }) => (
  <Box
    component={motion.div}
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      py: 6,
      px: 3,
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        backgroundColor: "hsl(140 10% 95%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Clock size={22} color={"hsl(150 10% 46%)"} strokeWidth={1.5} />
    </Box>
    <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
      {filtered ? "No matching activity" : "No activity yet"}
    </Typography>
    <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", textAlign: "center" }}>
      {filtered ? "Try other filters, dates or search terms" : "Events will appear here as they happen"}
    </Typography>
  </Box>
);

const HeaderToggle = ({ icon: Icon, label, open, active, onClick }) => (
  <Tooltip title={label} arrow placement="top">
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        position: "relative",
        width: 28,
        height: 28,
        borderRadius: "8px",
        color: open ? "hsl(150 38% 17%)" : "hsl(150 10% 46%)",
        backgroundColor: open ? "hsl(140 10% 95%)" : "transparent",
        "&:hover": { backgroundColor: "hsl(140 10% 95%)" },
      }}
    >
      <Icon size={14} strokeWidth={2.2} />
      {active && (
        <Box
          sx={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", backgroundColor: "hsl(142 60% 40%)" }}
        />
      )}
    </IconButton>
  </Tooltip>
);

const FilterBar = ({
  selectedFilters,
  onToggleFilter,
  counts,
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  showSearch,
  showDates,
}) => (
  <Box sx={{ px: 2, py: 1, borderBottom: "1px solid hsl(140 8% 88%)", display: "flex", flexDirection: "column", gap: 1 }}>
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        pr: 3,
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        maskImage: "linear-gradient(to right, #000 calc(100% - 24px), transparent)",
      }}
    >
      {FILTERS.map((f) => {
        const active = f.key === "all" ? selectedFilters.length === 0 : selectedFilters.includes(f.key);
        const count = f.key === "all" ? 0 : sumCounts(counts, FILTER_TYPES[f.key]);
        if (f.key !== "all" && count === 0 && !active) return null;
        const Icon = f.icon;
        return (
          <Box
            key={f.key}
            onClick={() => onToggleFilter(f.key)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: "999px",
              fontSize: 11.5,
              fontFamily: active ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
              color: active ? f.color : "hsl(150 10% 46%)",
              backgroundColor: active ? f.bg : "hsl(140 10% 95%)",
              border: `1px solid ${active ? `${f.color}25` : "hsl(140 8% 88%)"}`,
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              userSelect: "none",
              "&:hover": {
                backgroundColor: active ? f.bg : "hsl(140 10% 95%)",
                color: f.color,
              },
            }}
          >
            <Icon size={12} strokeWidth={2.2} />
            {f.label}
            {f.key !== "all" && count > 0 && (
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  color: active ? f.color : "hsl(150 10% 46%)",
                  backgroundColor: active ? `${f.color}12` : "transparent",
                  borderRadius: "999px",
                  px: active ? 0.5 : 0,
                  ml: 0.25,
                  lineHeight: 1.6,
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
    {(showSearch || showDates) && (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        {showSearch && (
          <TextField
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search timeline…"
            size="small"
            autoFocus
            InputProps={{ startAdornment: <Search size={14} color="hsl(150 10% 46%)" style={{ marginRight: 6 }} /> }}
            sx={{
              flex: "1 1 160px",
              "& .MuiInputBase-root": { fontSize: 12.5, borderRadius: "8px", height: 32 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
            }}
          />
        )}
        {showDates && (
          <DateRangeSelect startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={onDateRangeChange} />
        )}
      </Box>
    )}
  </Box>
);

const NoteComposer = ({ value, onChange, onSubmit, disabled, saving }) => {
  const hasContent = value.trim().length > 0;

  return (
    <Box
      sx={{
        borderTop: "1px solid hsl(140 8% 90%)",
        background: "linear-gradient(to top, hsl(140 10% 97%), #fff)",
        px: 1.5,
        pt: 1.25,
        pb: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1.5px solid hsl(140 8% 88%)",
          overflow: "hidden",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:focus-within": {
            borderColor: "hsl(150 35% 30%)",
            boxShadow: "0 0 0 3px hsla(150, 38%, 17%, 0.06), 0 1px 4px hsla(150, 30%, 20%, 0.06)",
          },
        }}
      >
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Write a note…"
          fullWidth
          variant="standard"
          multiline
          minRows={1}
          maxRows={8}
          InputProps={{ disableUnderline: true }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !disabled) {
              e.preventDefault();
              onSubmit();
            }
          }}
          sx={{
            px: 1.75,
            pt: 1.25,
            pb: 0.5,
            "& .MuiInputBase-input": {
              fontSize: 13,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(150 30% 12%)",
              lineHeight: 1.6,
            },
            "& textarea::placeholder": {
              color: "hsl(150 10% 58%)",
              opacity: 1,
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.25,
            pb: 1,
            pt: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: hasContent ? "hsl(150 10% 52%)" : "transparent",
              transition: "color 0.2s ease",
              userSelect: "none",
              pl: 0.5,
            }}
          >
            ⌘/Ctrl + Enter to send
          </Typography>
          <Tooltip title={saving ? "Sending…" : "Send note"} arrow placement="top">
            <span>
              <IconButton
                onClick={onSubmit}
                disabled={disabled}
                disableRipple
                sx={{
                  backgroundColor: hasContent ? "hsl(150 38% 17%)" : "hsl(140 8% 88%)",
                  color: hasContent ? "hsl(80 97% 72%)" : "hsl(150 10% 58%)",
                  borderRadius: "10px",
                  width: 32,
                  height: 32,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: hasContent ? "hsl(150 38% 22%)" : "hsl(140 8% 85%)",
                    transform: hasContent ? "translateY(-1px)" : "none",
                    boxShadow: hasContent ? "0 2px 8px hsla(150, 38%, 17%, 0.18)" : "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "hsl(140 8% 90%)",
                    color: "hsl(150 10% 68%)",
                  },
                }}
              >
                <Send size={14} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

const CompanyTimelinePanel = ({ companyId, refreshTrigger }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);

  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [nextCursor, setNextCursor] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [searchOpen, setSearchOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const feedRef = useRef(null);
  const sentinelRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const timelineParams = useMemo(
    () => ({
      types: selectedFilters.flatMap((key) => FILTER_TYPES[key] || [key]),
      q: query || undefined,
      from: dateRange.startDate || undefined,
      to: dateRange.endDate || undefined,
      limit: PAGE_SIZE,
    }),
    [selectedFilters, query, dateRange]
  );

  useEffect(() => {
    if (companyId == null) return;

    const requestId = ++requestRef.current;
    setLoading(true);
    sh.get(`/internal/companies/${companyId}/timeline`, { params: timelineParams })
      .then((res) => {
        if (requestId !== requestRef.current) return;
        setEvents(res.data.events || []);
        setNextCursor(res.data.next_cursor || null);
        setCounts(res.data.counts || {});
      })
      .catch(() => {
        if (requestId !== requestRef.current) return;
        setEvents([]);
        setNextCursor(null);
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [companyId, refreshTrigger, timelineParams]);

  const loadMore = useCallback(() => {
    if (!nextCursor || loading || loadingMore) return;

    const requestId = requestRef.current;
    setLoadingMore(true);
    sh.get(`/internal/companies/${companyId}/timeline`, { params: { ...timelineParams, before: nextCursor } })
      .then((res) => {
        if (requestId !== requestRef.current) return;
        setEvents((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          return [...prev, ...(res.data.events || []).filter((e) => !seen.has(e.id))];
        });
        setNextCursor(res.data.next_cursor || null);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [companyId, timelineParams, nextCursor, loading, loadingMore]);

  // Load the next page when the bottom of the feed scrolls into view
  useEffect(() => {
    const root = feedRef.current;
    const target = sentinelRef.current;
    if (!root || !target || !nextCursor) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore, nextCursor, events.length]);

  const showSearch = searchOpen || search !== "";
  const showDates = datesOpen || !!dateRange.startDate || !!dateRange.endDate;

  const toggleSearch = () => {
    if (showSearch) setSearch("");
    setSearchOpen(!showSearch);
  };

  const toggleDates = () => {
    if (showDates) setDateRange({ startDate: null, endDate: null });
    setDatesOpen(!showDates);
  };

  const toggleFilter = (key) => {
    if (key === "all") {
      setSelectedFilters([]);
      return;
    }
    setSelectedFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const isFiltered = selectedFilters.length > 0 || !!query || !!dateRange.startDate || !!dateRange.endDate;
  const totalCount = selectedFilters.length
    ? sumCounts(counts, selectedFilters.flatMap((key) => FILTER_TYPES[key] || [key]))
    : Object.values(counts).reduce((sum, n) => sum + n, 0);
  const notesVisible = selectedFilters.length === 0 || selectedFilters.includes("notes");

  const dateGroups = useMemo(() => groupEventsByDate(events), [events]);

  const addNote = () => {
    const content = draft.trim();
    if (!content || companyId == null) return;

    setSaving(true);
    sh.post(`/internal/companies/${companyId}/notes`, { note: { content }, authenticity_token })
      .then((res) => {
        const newEvent = {
          id: `note_${res.data.id}`,
          type: "note",
          occurred_at: res.data.created_at,
          actor: { type: "author", id: null, name: res.data.author },
          payload: { content: res.data.content, editable: res.data.editable },
        };
        if (notesVisible) setEvents((prev) => [newEvent, ...prev]);
        setCounts((prev) => ({ ...prev, note: (prev.note || 0) + 1 }));
        setDraft("");
        if (feedRef.current) feedRef.current.scrollTop = 0;
      })
      .finally(() => setSaving(false));
  };

  const submitDisabled = saving || draft.trim().length === 0 || companyId == null;

  const editNote = (noteId, newContent) => {
    sh.patch(`/internal/companies/${companyId}/notes/${noteId}`, {
      authenticity_token,
      note: { content: newContent },
    }).then(() => {
      setEvents((prev) =>
        prev.map((e) =>
          e.id === `note_${noteId}` ? { ...e, payload: { ...e.payload, content: newContent } } : e
        )
      );
    });
  };

  const archiveNote = (noteId) => setArchiveTargetId(noteId);

  const confirmArchive = () => {
    if (!archiveTargetId) return;
    sh.delete(`/internal/companies/${companyId}/notes/${archiveTargetId}`, {
      data: { authenticity_token },
    }).then(() => {
      setEvents((prev) => prev.filter((e) => e.id !== `note_${archiveTargetId}`));
      setCounts((prev) => ({ ...prev, note: Math.max((prev.note || 0) - 1, 0) }));
      setArchiveTargetId(null);
    });
  };

  let globalIndex = 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        overflow: "hidden",
        height: "100%",
        maxHeight: "calc(100vh - 110px)",
        minHeight: 360,
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.25,
          borderBottom: "1px solid hsl(140 8% 88%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            sx={{
              fontSize: 14,
              fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              color: "hsl(150 30% 12%)",
              letterSpacing: -0.1,
            }}
          >
            Timeline
          </Typography>
          <Box
            sx={{
              px: 0.75,
              py: 0.125,
              borderRadius: "999px",
              backgroundColor: "hsl(140 10% 95%)",
              border: "1px solid hsl(140 8% 88%)",
            }}
          >
            <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
              {totalCount}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <HeaderToggle
            icon={Search}
            label={showSearch ? "Clear search" : "Search"}
            open={showSearch}
            active={!!search}
            onClick={toggleSearch}
          />
          <HeaderToggle
            icon={CalendarDays}
            label={showDates ? "Clear dates" : "Filter by date"}
            open={showDates}
            active={!!(dateRange.startDate || dateRange.endDate)}
            onClick={toggleDates}
          />
        </Stack>
      </Box>

      <FilterBar
        selectedFilters={selectedFilters}
        onToggleFilter={toggleFilter}
        counts={counts}
        search={search}
        onSearchChange={setSearch}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        showSearch={showSearch}
        showDates={showDates}
      />

      <Box
        ref={feedRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "hsl(140 8% 85%)",
            borderRadius: 3,
            "&:hover": { background: "hsl(150 10% 46%)" },
          },
        }}
      >
        {loading ? (
          <FeedSkeleton />
        ) : events.length === 0 ? (
          <EmptyFeed filtered={isFiltered} />
        ) : (
          <Box sx={{ pb: 1 }}>
            {dateGroups.map((group) => {
              const groupDelay = globalIndex * 0.04;
              return (
                <Box key={group.label}>
                  <DateGroupHeader
                    label={group.label}
                    delay={Math.min(groupDelay, 0.3)}
                    animated={globalIndex < PAGE_SIZE}
                  />
                  <Box sx={{ pl: 2 }}>
                    {group.events.map((e, i) => {
                      const delay = Math.min(globalIndex * 0.04, 0.4);
                      const isLast = i === group.events.length - 1;
                      const animated = globalIndex < PAGE_SIZE;
                      globalIndex++;
                      return (
                        <EventRow
                          key={e.id}
                          event={e}
                          onEditNote={editNote}
                          onArchiveNote={archiveNote}
                          isLast={isLast}
                          delay={delay}
                          animated={animated}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
            <Box ref={sentinelRef} sx={{ height: 1 }} />
            {loadingMore && <FeedSkeleton rows={2} />}
            {!nextCursor && (
              <Typography
                sx={{
                  fontSize: 11,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(150 10% 58%)",
                  textAlign: "center",
                  py: 1.5,
                }}
              >
                Beginning of timeline
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <NoteComposer
        value={draft}
        onChange={setDraft}
        onSubmit={addNote}
        disabled={submitDisabled}
        saving={saving}
      />

      <ConfirmDialog
        open={archiveTargetId != null}
        onClose={() => setArchiveTargetId(null)}
        onConfirm={confirmArchive}
        title="Archive note"
        description="Are you sure you want to archive this note? This action cannot be undone."
        confirmLabel="Archive"
        destructive
      />
    </Box>
  );
};

export default CompanyTimelinePanel;
